import { useTranslation } from 'react-i18next'
import { useCallback, useMemo } from 'react'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { Deal, DealType, StrapiID } from '@ca/common/types'
import { keyBy } from 'lodash'
import AppConfig from 'config'
import { resolveDevToggle, useDevToggles } from 'hooks/dev/useDevToggles'
import {
  useConfigConditionDealCheck,
  useConfigConditionGroupCheckBase,
} from 'features/config/useConfigCondition'

// const TAG_DEAL_ACTIVE = 'active' // @deprecated
const TAG_DEAL_HIDDEN = 'hidden'

export const useLocalizedDeals = () => {
  const config = useRemoteConfig()
  const { i18n } = useTranslation()

  return useMemo(() => {
    if (!config) {
      return undefined
    }
    let localized = config.deals[i18n.language]
    if (!localized) {
      localized = config.deals['de']
    }
    return keyBy(config.deals[i18n.language], 'id')
  }, [config, i18n.language])
}

/**
 * Take dev value if set
 * If in tgbo mode, only tgbo deals are allowed
 * else use app config deal types
 */
const getAllowedDealTypes = ({
  customDealTypes,
}: {
  customDealTypes?: string[]
}) => {
  if (customDealTypes) {
    return customDealTypes
  }
  return AppConfig.dealTypes
}

export const useAllowedDealTypes = () => {
  const { dealTypes: devDealTypesToggle } = useDevToggles()
  const devDealTypes = devDealTypesToggle?.split(',') as DealType[] | undefined

  return resolveDevToggle(devDealTypes, AppConfig.dealTypes)
}

export const isValidDealType = (
  type: DealType | null | undefined,
  allowedTypes: string[]
) => {
  if (!type && allowedTypes.includes('normal')) {
    return true
  }
  if (!type) {
    return false
  }
  return allowedTypes.includes(type)
}

export const useCheckIsDealHidden = () => {
  const checkDeal = useConfigConditionDealCheck()
  const { checkGroup, getGroup } = useConfigConditionGroupCheckBase()

  // Just get the group once
  const group = useMemo(() => getGroup('deal:isHidden'), [getGroup])

  return useCallback(
    (deal: Deal) => {
      return checkGroup((name) => checkDeal(name, deal), group)
    },
    [checkDeal, group, checkGroup]
  )
}

export const useDeals = ({
  hidden,
  filterCards,
}: {
  hidden?: boolean
  filterCards?: boolean
}) => {
  const config = useRemoteConfig()
  const { i18n } = useTranslation()
  const dealLang = AppConfig.dealLang || i18n.language
  const allowedDealTypes = useAllowedDealTypes()
  const checkDeal = useConfigConditionDealCheck()
  const checkIsDealHidden = useCheckIsDealHidden()

  const deals = useMemo(() => {
    const deals = config?.deals
    if (!deals) return []
    const localized = deals[dealLang]

    const isFilterCard = (deal: Deal) => {
      if (deal.filter) {
        return true
      }

      return checkDeal('intro:isLink', deal)
    }

    const isValidFilter = (deal: Deal) => {
      if (!deal.filter?.type) {
        return true
      }
      return allowedDealTypes.includes(deal.filter.type)
    }

    const filter = (deal: Deal) => {
      if (filterCards !== undefined) {
        const isFilter = isFilterCard(deal)
        if (isFilter) {
          return filterCards && isValidFilter(deal)
        }
      }

      const tags = deal.tags

      if (deal.packages.length === 0) {
        return false
      }

      if (hidden === false && tags?.includes(TAG_DEAL_HIDDEN)) {
        return false
      }

      if (!isValidDealType(deal.type, allowedDealTypes)) {
        return false
      }

      if (checkIsDealHidden(deal).any) {
        return false
      }

      return true
    }

    if (!localized) return []

    return localized.filter(filter)
  }, [
    config?.deals,
    hidden,
    dealLang,
    allowedDealTypes,
    checkDeal,
    filterCards,
    checkIsDealHidden,
  ])

  return deals
}

export const useDealsById = (): Record<StrapiID, Deal> => {
  const config = useRemoteConfig()

  return useMemo(() => {
    if (!config?.deals) {
      return {}
    }
    const allArr = Object.values(config?.deals).flat()

    return keyBy(allArr, 'id') satisfies Record<StrapiID, Deal>
  }, [config?.deals])
}

export default useDeals
